import { useState } from "react";
import type { Perk } from "@dropin/shared/schemas";
import { AvatarUploader } from "./AvatarUploader";
import { Toggle } from "./Toggle";

export type PerkFormValues = {
  title: string;
  description: string;
  imageUrl: string | null;
  redemptionCode: string | null;
  requireApproval: boolean;
};

export function PerkForm({
  businessId,
  initial,
  onSubmit,
  onCancel,
  submitLabel,
  submittingLabel,
  testidPrefix = "perk",
}: {
  businessId: string;
  // When provided, the form is in edit mode (pre-filled, no auto-reset on save).
  initial?: Perk;
  onSubmit: (values: PerkFormValues) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  submittingLabel?: string;
  // Test id prefix — defaults are tuned for the create form; PerkCard
  // edit overrides with `perk-edit-${perkId}` for parity with the prior layout.
  testidPrefix?: string;
}) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [imageUrl, setImageUrl] = useState<string | null>(initial?.imageUrl ?? null);
  const [redemptionCode, setRedemptionCode] = useState(initial?.redemptionCode ?? "");
  const [requireApproval, setRequireApproval] = useState(initial?.requireApproval ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        title,
        description,
        imageUrl: imageUrl || null,
        redemptionCode: redemptionCode.trim() || null,
        requireApproval,
      });
      if (!isEdit) {
        setTitle("");
        setDescription("");
        setImageUrl(null);
        setRedemptionCode("");
        setRequireApproval(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "failed";
      // Pretty up the free-plan error so the user knows what to do.
      if (msg.includes("402") || msg.includes("free_plan_limit")) {
        setError(
          "Free plan allows 1 active perk at a time. Deactivate the existing perk, or upgrade to Pro for unlimited perks.",
        );
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  const defaultSubmit = isEdit ? "Save changes" : "Post perk";
  const defaultSubmitting = isEdit ? "Saving…" : "Posting…";

  return (
    <form onSubmit={submit} className="grid gap-3" data-testid={`${testidPrefix}-form`}>
      <label className="grid gap-1.5">
        <FieldLabel>Title</FieldLabel>
        <input
          className="input"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Free pour-over"
          data-testid={`${testidPrefix}-title`}
        />
      </label>
      <AvatarUploader
        value={imageUrl}
        onChange={setImageUrl}
        kind="perk-images"
        scopeId={businessId}
        fallback={title || "?"}
        shape="square"
        label="Perk image (optional)"
        hint="Shown to creators on browse and after they claim."
        testid={`${testidPrefix}-image`}
      />
      <label className="grid gap-1.5">
        <FieldLabel>Description / ask</FieldLabel>
        <textarea
          className="input min-h-[88px]"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="One Reel within 48h, tag @us"
          data-testid={`${testidPrefix}-description`}
        />
      </label>
      <label className="grid gap-1.5">
        <FieldLabel>Redemption code (optional)</FieldLabel>
        <input
          className="input font-mono uppercase tracking-wider"
          value={redemptionCode}
          onChange={(e) => setRedemptionCode(e.target.value.toUpperCase())}
          placeholder="DROP24"
          maxLength={40}
          data-testid={`${testidPrefix}-code`}
        />
        <span className="text-xs text-muted">
          Creators see this only after the claim is approved — they show it to staff at the store.
        </span>
      </label>
      <div className="flex items-start gap-3 rounded-md border border-border bg-paper px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Require approval</p>
          <p className="mt-0.5 text-xs text-muted">
            Off (default): claims are approved instantly — creators see the redemption code right away. On: the business reviews each claim before the code is revealed.
          </p>
        </div>
        <Toggle
          active={requireApproval}
          onChange={setRequireApproval}
          testid={`${testidPrefix}-require-approval`}
        />
      </div>
      {error && <p className="text-sm text-rose">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="btn-primary"
          data-testid={`${testidPrefix}-submit`}
        >
          {saving ? (submittingLabel ?? defaultSubmitting) : (submitLabel ?? defaultSubmit)}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn text-sm"
            onClick={onCancel}
            disabled={saving}
            data-testid={`${testidPrefix}-cancel`}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wider text-muted">
      {children}
    </span>
  );
}
